import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import MuiLongTextField from '@brands/Components/Inputs/MuiLongTextField/MuiLongTextField';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import PhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { UnifiedProfileSubmit } from '@brands/Utils/types';
import { yupResolver } from '@hookform/resolvers/yup';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Alert, AlertTitle, ToggleButton, ToggleButtonGroup } from '@mui/material';
import cn from 'classnames';
import { E164Number, parsePhoneNumber } from 'libphonenumber-js';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import SuccessAlert from '../../../../../Components/Alert/SuccessAlert';
import Button from '../../../../../Components/Button/Button';
import ProfileImage from '../../../../../Components/ProfileImage/ProfileImage';
import { RegisterMediaPayload } from '../../../../../services/communication/registersConversationMedia';
import { confirmAvatarMedia } from '../../../../../services/identity/confirmAvatarMedia';
import { createBasicInformation } from '../../../../../services/identity/createBasicInformation';
import { createCareAdvisorProfile } from '../../../../../services/identity/createCareAdvisorProfile';
import { createNewUser, fullNameFromFirstAndLast } from '../../../../../services/identity/createNewUser';
import { updateUserClientTag } from '../../../../../services/identity/updateUserClientTag';
import {
  getAllowedDivisionIds,
  getClientDivisionOptions,
} from '../../../../../services/identity/clientDivisionOptions';
import {
  getNetworkOptionsForRole,
  listNetworkOptions,
  NetworkOption,
} from '../../../../../services/identity/networkOptions';
import { updateUserNetwork } from '../../../../../services/identity/updateUserNetwork';
import { deleteAvatar } from '../../../../../services/identity/deleteAvatar';
import { listAllClientTags } from '../../../../../services/identity/listAllClientTags';
import { scopeClientTagOptionsToActor } from '../../../../../services/identity/clientTagActorScope';
import { listAllLanguages } from '../../../../../services/identity/listAllLanguages';
import { registerAvatarMedia } from '../../../../../services/identity/registerAvatarMedia';
import {
  Address,
  BasicInfo,
  ClientTag,
  Language,
  UserProfile,
  UserRoleName,
} from '../../../../../services/identity/types/UserProfile';
import { displayErrorDetails, withoutErrorCode } from '../../../../../Utils/displayError';
import { genderIdentity, genderOptions, pronounsOptions } from '../../../../../Utils/selectButtonOptions';
import { OptionLanguage } from '../../../../../Utils/selectOptions';
import { defaultFormValues } from '../../../../MyProfile/CareAdvisor/utils/defaultFromValues';
import { CAMyProfileSubmit, ClientTagOption } from '../../../../MyProfile/CareAdvisor/utils/types';
import { updateData } from '../../../../MyProfile/Patient/utils/utilsFunctions';
import styles from '../styles.module.scss';
import { validationSchema } from './validationSchema';

interface ProfileCardProps {
  onClose: () => void;
}

const AddCareAdvisorUserPage: FC<ProfileCardProps> = ({ onClose }) => {
  const { userInfo } = useAppSelector(selectAuth);

  const [myProfileForm, setMyProfileForm] = useState<CAMyProfileSubmit>(defaultFormValues as CAMyProfileSubmit);
  const [currentAvatarURL, setCurrentAvatarURL] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPayload, setAvatarPayload] = useState<RegisterMediaPayload | null>(null);
  const [selection, setSelection] = React.useState<[number | null, number | null] | null>(null);
  const ref = React.useRef<HTMLInputElement>(null);
  const [languageOptions, setLanguageOptions] = useState<OptionLanguage[]>([]);
  const [clientTagOptions, setClientTagOptions] = useState<ClientTagOption[]>([]);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);
  const [networkOptions, setNetworkOptions] = useState<NetworkOption[]>([]);
  const [avatarAction, setAvatarAction] = useState<string>('');
  const [image, setImage] = React.useState<string>();
  const [isSendingInvitation, setIsSendingInvitation] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const closePage = (): void => {
    onClose();
  };

  const canEditNetwork = userInfo.role.name === UserRoleName.Admin || userInfo.role.name === UserRoleName.SuperAdmin;
  // Required for every role once the field is actually on the page (mirrors the
  // `clientTagOptions.length > 0` visibility check on the Client tag select below) --
  // requiredness used to be gated to CareAdvisor/Collector/OrganizationAdmin only.
  const clientTagsRequired = useMemo(() => clientTagOptions.length > 0, [clientTagOptions]);
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
    watch,
  } = useForm<CAMyProfileSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema(clientTagsRequired)),
  });

  const watchedClientTagIds = watch('client_tag_ids') || [];
  const divisionOptions = useMemo(
    () =>
      getClientDivisionOptions(allClientTags, watchedClientTagIds).map((option) => ({
        id: option.id,
        value: String(option.value),
        label: option.label,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allClientTags, JSON.stringify(watchedClientTagIds)],
  );
  const selectedClientTagIds = (watchedClientTagIds || []).map((id) => String(id));

  const triggerProfileAvatar = React.useCallback((avatarUrl: string) => {
    setValue('profile_img', avatarUrl, { shouldValidate: true });
    setMyProfileForm({
      ...getValues(),
      profile_img: avatarUrl,
    });
  }, []);

  const fetchLanguageOptions = async (): Promise<void> => {
    try {
      const data = await listAllLanguages();
      const transformedData = data.map((language) => ({
        value: language.locale,
        label: language.name,
        id: language.id,
      }));
      setLanguageOptions(transformedData);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
  };

  useEffect(() => {
    fetchLanguageOptions();
    listAllClientTags()
      .then(({ nodes }) => {
        const mapped = (nodes || []).map((c) => ({ id: String(c.id), value: c.id, label: c.name }));
        setClientTagOptions(scopeClientTagOptionsToActor(mapped, userInfo));
        setAllClientTags(nodes || []);
      })
      .catch(() => {
        setClientTagOptions([]);
        setAllClientTags([]);
      });
    if (canEditNetwork) {
      listNetworkOptions(userInfo.role.name)
        .then((catalog) => setNetworkOptions(getNetworkOptionsForRole(catalog, UserRoleName.CareAdvisor)))
        .catch(() => setNetworkOptions([]));
    }
  }, []);

  const setTextValue = (e: React.ChangeEvent<HTMLInputElement>, name: string): void => {
    const { value } = e.target;
    setValue(name as keyof CAMyProfileSubmit, value, { shouldValidate: true });
    setSelection([e.target.selectionStart, e.target.selectionEnd]);
  };

  React.useLayoutEffect(() => {
    if (selection && ref.current) {
      [ref.current.selectionStart, ref.current.selectionEnd] = selection;
    }
  }, [selection]);

  useEffect(() => {
    if (Object.keys(myProfileForm).length > 0) {
      Object.keys(myProfileForm).forEach((key: unknown) => {
        setValue(key as keyof CAMyProfileSubmit, myProfileForm[key as keyof CAMyProfileSubmit]);
      });
    }
  }, [myProfileForm, setValue]);

  const onInvalid = (errorsList: Record<string, { message?: string }>): void => {
    const error = Object.values(errorsList)[0];
    if (error?.message) {
      toast.error(error.message, { className: 'fs-unmask' });
    }
  };

  const onCancelEditProfile = (): void => {
    onClose();
    setImage(currentAvatarURL);
  };

  const callSaveProfile = async (profileData: UnifiedProfileSubmit): Promise<void> => {
    const typedProfile = profileData as CAMyProfileSubmit;
    let newUser: UserProfile | undefined;
    setIsSavingProfile(true);
    const fullNameForCreateUser = fullNameFromFirstAndLast(typedProfile.first_name, typedProfile.last_name);
    const client_ids = (typedProfile.client_tag_ids || [])
      .map((id) => Number(id))
      .filter((n) => Number.isInteger(n) && n > 0);
    try {
      newUser = await createNewUser({
        email: profileData.email?.toLowerCase(),
        ...(typedProfile.phone_number && {
          phone_number: parsePhoneNumber(typedProfile.phone_number, 'US')?.format('E.164') as string,
        }),
        ...(profileData.address1 !== '' ||
        profileData.address2 !== '' ||
        profileData.city !== '' ||
        profileData.state !== '' ||
        profileData.zip_code !== ''
          ? {
              address: {
                ...(profileData.address1 !== '' && { address1: profileData.address1 }),
                ...(profileData.address2 !== '' && { address2: profileData.address2 }),
                ...(profileData.city !== '' && { city: profileData.city }),
                ...(profileData.state !== '' && { state: profileData.state }),
                ...(profileData.zip_code !== '' && { zip_code: profileData.zip_code }),
              } as Address,
            }
          : {}),
        type: UserRoleName.CareAdvisor,
        send_invitation: isSendingInvitation,
        ...(fullNameForCreateUser ? { full_name: fullNameForCreateUser } : {}),
        // Every user belongs to Dentistry One; the backend adds its default division/practice.
        organization_ids: ['dentistry-one'],
        ...(client_ids.length > 0 && { client_ids }),
      });
    } catch (error: unknown) {
      displayErrorDetails(error);
      setIsSavingProfile(false);
    }
    if (newUser) {
      try {
        const client_division_ids = (typedProfile.client_division_ids || [])
          .map((id) => Number(id))
          .filter((n) => Number.isInteger(n) && n > 0);
        if (client_division_ids.length > 0) {
          await updateUserClientTag(newUser.id, { client_ids, client_division_ids });
        }
        const selectedNetwork = (typedProfile as unknown as { network?: string | null }).network;
        if (canEditNetwork && selectedNetwork) {
          await updateUserNetwork(newUser.id, selectedNetwork, userInfo.role.name);
        }
        if (avatarPayload && avatarFile && avatarAction === 'Upload') {
          const res = await registerAvatarMedia(avatarPayload);
          const mediaId = res.files[0].upload_id;
          await fetch(res.files[0].upload_url, {
            method: 'PUT',
            body: avatarFile,
            headers: { 'content-type': avatarFile.type },
          });
          await confirmAvatarMedia(mediaId, newUser.id);
          triggerProfileAvatar(res.files[0].upload_url);
        } else if (avatarAction === 'Remove') {
          await deleteAvatar(newUser.id);
          triggerProfileAvatar('');
        }
        const dataWithUpdatedDOB = updateData(typedProfile as BasicInfo);
        if (
          dataWithUpdatedDOB.first_name !== '' ||
          dataWithUpdatedDOB.middle_name !== '' ||
          dataWithUpdatedDOB.last_name !== '' ||
          dataWithUpdatedDOB.gender !== '' ||
          dataWithUpdatedDOB.pronouns !== '' ||
          (Array.isArray(dataWithUpdatedDOB.languages) &&
            dataWithUpdatedDOB.languages.length > 0 &&
            (dataWithUpdatedDOB.languages[0] as Language).name !== '')
        ) {
          const basicInfoPayload: Record<string, unknown> = {
            ...(dataWithUpdatedDOB.first_name && { first_name: dataWithUpdatedDOB.first_name }),
            ...(dataWithUpdatedDOB.middle_name && { middle_name: dataWithUpdatedDOB.middle_name }),
            ...(dataWithUpdatedDOB.last_name && { last_name: dataWithUpdatedDOB.last_name }),
            ...(dataWithUpdatedDOB.gender && { gender: dataWithUpdatedDOB.gender }),
            ...(dataWithUpdatedDOB.pronouns && { pronouns: dataWithUpdatedDOB.pronouns }),
            ...(dataWithUpdatedDOB.gender_identity && { gender_identity: dataWithUpdatedDOB.gender_identity }),
            ...(typedProfile.language && {
              languages: typedProfile.language.map((language) => language.id),
            }),
          };
          await createBasicInformation(basicInfoPayload as BasicInfo, newUser.id);
        }

        const updates: Partial<CAMyProfileSubmit> = {};

        if (typedProfile.biography !== undefined) {
          updates.biography = typedProfile.biography;
        }

        if (typedProfile.phone_number !== undefined) {
          updates.phone_number = typedProfile.phone_number;
        }

        (['address1', 'address2', 'city', 'state', 'zip_code'] as const).forEach((key) => {
          const value = typedProfile[key];
          if (typeof value === 'string' && value !== '') {
            updates[key] = value;
          }
        });

        if (Object.keys(updates).length > 0) {
          newUser = await createCareAdvisorProfile(updates, newUser.id);
        }

        toast.success('Care advisor user created.', { className: 'fs-unmask' });
        onClose();
      } catch (error: any) {
        if (error.response) {
          if (error.response.data.message && error.response.data.details) {
            toast.error(withoutErrorCode(`${error.response.data.message} \n${error.response.data.details}`), {
              className: 'fs-unmask',
            });
          } else if (error.response.data.details) {
            toast.error(withoutErrorCode(error.response.data.details), { className: 'fs-unmask' });
          } else {
            toast.error(withoutErrorCode(error.response.data.message), { className: 'fs-unmask' });
          }
        } else {
          toast.error(error.message, { className: 'fs-unmask' });
        }
      }
    }
    setIsSavingProfile(false);
  };

  const onSubmitProfile = async (profileData: CAMyProfileSubmit): Promise<void> => {
    await callSaveProfile(profileData);
  };

  const formContent = (
    <>
      <div className={cn(styles.profileContainer, styles.newDesignProfileContainer, styles.providerPixelPerfect)}>
        <div className={styles.contentContainer}>
          <div className={styles.contentContainerInner}>
            <div className={styles.editProfileContainer}>
              <div className={styles.backButtonContainer}>
                <button type="button" className={styles.backButton} onClick={onClose}>
                  <ChevronLeftIcon className={styles.backButtonIcon} aria-hidden />
                  Back
                </button>
              </div>
              <Alert severity="info" className={styles.createUserNotice} style={{ marginBottom: '24px' }}>
                <AlertTitle>Creating care advisor profile</AlertTitle>
              </Alert>
              <div className={styles.providerInviteSection}>
                <div className={styles.providerInviteTitle}>Send invitation to user?</div>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={isSendingInvitation ? 'yes' : 'no'}
                  onChange={(_, value) => {
                    if (value !== null) {
                      setIsSendingInvitation(value === 'yes');
                    }
                  }}
                  className={styles.providerInviteToggle}
                >
                  <ToggleButton value="yes">Yes</ToggleButton>
                  <ToggleButton value="no">No</ToggleButton>
                </ToggleButtonGroup>
              </div>
              <div className={styles.separator} />
              <div className={styles.titleContainer}>
                <span className={styles.title}>Basic information</span>
              </div>
              <div className={styles.editContainer}>
                <form className={styles.formContainer} onSubmit={handleSubmit(onSubmitProfile, onInvalid)}>
                  <div className={styles.headerActionDiv}>
                    <ProfileImage
                      reviewProfile
                      setCurrentAvatarURL={setCurrentAvatarURL}
                      setAvatarFile={setAvatarFile}
                      setAvatarPayload={setAvatarPayload}
                      setAvatarAction={setAvatarAction}
                      triggerProfileAvatar={triggerProfileAvatar}
                      setImage={setImage}
                      image={image}
                      errors={errors}
                      size="80px"
                      isNewDependent
                    />
                  </div>

                  <div className={styles.row}>
                    <div className={cn(styles.rowItem, styles.detailsCA)}>
                      <TextField
                        {...register('first_name')}
                        label="First Name"
                        required
                        errors={errors}
                        name="first_name"
                        value={getValues('first_name')}
                        onChange={(e) => setTextValue(e, 'first_name')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                    <div className={cn(styles.rowItem, styles.detailsCA)}>
                      <TextField
                        {...register('middle_name')}
                        label="Middle Name"
                        errors={errors}
                        name="middle_name"
                        value={getValues('middle_name')}
                        onChange={(e) => setTextValue(e, 'middle_name')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                    <div className={cn(styles.rowItem, styles.detailsCA)}>
                      <TextField
                        {...register('last_name')}
                        label="Last Name"
                        required
                        errors={errors}
                        name="last_name"
                        value={getValues('last_name')}
                        onChange={(e) => setTextValue(e, 'last_name')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={cn(styles.rowItem, styles.details)}>
                      <MUISelectInput
                        {...register('gender')}
                        name="gender"
                        label="Gender assigned at birth"
                        options={genderOptions}
                        wrapperClassName={styles.inputWrapper}
                        errors={errors}
                        value={getValues('gender') || ''}
                        onChange={(value: string) => {
                          setValue('gender', value, { shouldValidate: true });
                        }}
                      />
                    </div>
                    <div className={cn(styles.rowItem, styles.details)}>
                      <MUISelectInput
                        {...register('gender_identity')}
                        name="gender_identity"
                        label="Gender identity"
                        options={genderIdentity}
                        wrapperClassName={styles.inputWrapper}
                        errors={errors}
                        value={getValues('gender_identity') || ''}
                        onChange={(value: string) => {
                          if (value) {
                            setValue('gender_identity', value, { shouldValidate: true });
                          }
                        }}
                      />
                    </div>
                    <div className={cn(styles.rowItem, styles.details)}>
                      <MUISelectInput
                        {...register('pronouns')}
                        name="pronouns"
                        label="Pronouns (optional)"
                        options={pronounsOptions}
                        wrapperClassName={styles.inputWrapper}
                        errors={errors}
                        value={getValues('pronouns') || ''}
                        onChange={(value: string) => {
                          if (value) {
                            setValue('pronouns', value, { shouldValidate: true });
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={cn(styles.rowItem, styles.rowItemHalf)}>
                      <MultiSelectInputMUI
                        {...register('language')}
                        name="language"
                        label="Languages"
                        withId
                        options={languageOptions}
                        wrapperClassName={styles.inputWrapper}
                        errors={errors}
                        value={(getValues('language') || []).map((language: any) => String(language.id))}
                        onChange={(ids: string[]) => {
                          const selected = languageOptions.filter((option) => ids.includes(String(option.id)));
                          setValue('language', selected, {
                            shouldValidate: true,
                          });
                        }}
                      />
                    </div>
                    <div className={cn(styles.rowItem, styles.rowItemHalf)}>
                      <div className={styles.inputWrapper}>
                        <PhoneInput
                          {...register('phone_number')}
                          label="Phone Number"
                          id="phone_number"
                          value={getValues('phone_number') as E164Number}
                          onChange={(val) => {
                            setValue('phone_number', val as string, {
                              shouldValidate: true,
                            });
                          }}
                          inputClass={styles.input}
                          errors={errors}
                          wrapperClassName={styles.inputWrapper}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={cn(styles.rowItem)}>
                      <TextField
                        {...register('email')}
                        label="Email Address"
                        required
                        errors={errors}
                        name="email"
                        value={getValues('email')}
                        onChange={(e) => setTextValue(e, 'email')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={cn(styles.rowItem, styles.details)}>
                      <TextField
                        {...register('zip_code')}
                        label="Zip code"
                        errors={errors}
                        name="zip_code"
                        value={getValues('zip_code')}
                        onChange={(e) => setTextValue(e, 'zip_code')}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={cn(styles.rowItem)}>
                      <MuiLongTextField
                        {...register('biography')}
                        name="biography"
                        label="Biography"
                        multiline
                        minRows={6}
                        placeholder="Write here"
                        errors={errors}
                        value={getValues('biography')}
                        onChange={(e) => {
                          setValue('biography', e.target.value, { shouldValidate: true });
                          setSelection([e.target.selectionStart, e.target.selectionEnd]);
                        }}
                        wrapperClassName={styles.inputWrapper}
                        inputClass={styles.input}
                      />
                    </div>
                  </div>
                  <div className={cn(styles.row, styles.providerOrganizationRow)}>
                    <div className={cn(styles.rowItem, styles.details, styles.big)}>
                      <MultiSelectInputMUI
                        {...register('client_tag_ids')}
                        name="client_tag_ids"
                        label="Client tag(s)"
                        withId
                        options={clientTagOptions}
                        required={clientTagsRequired}
                        wrapperClassName={styles.inputWrapper}
                        errors={errors}
                        value={clientTagOptions.length > 0 ? selectedClientTagIds : []}
                        onChange={(ids: string[]) => {
                          setValue('client_tag_ids', ids, { shouldValidate: true });
                          const allowedDivisionIds = getAllowedDivisionIds(allClientTags, ids);
                          setValue(
                            'client_division_ids',
                            (watch('client_division_ids') || []).filter((id) => allowedDivisionIds.has(String(id))),
                            { shouldValidate: true },
                          );
                        }}
                      />
                    </div>
                  </div>
                  {watchedClientTagIds.length > 0 && divisionOptions.length > 0 && (
                    <div className={cn(styles.row, styles.providerOrganizationRow)}>
                      <div className={cn(styles.rowItem, styles.details, styles.big)}>
                        <MultiSelectInputMUI
                          name="client_division_ids"
                          label="Division(s)"
                          withId
                          options={divisionOptions}
                          wrapperClassName={styles.inputWrapper}
                          errors={errors}
                          value={watch('client_division_ids') || []}
                          onChange={(ids: string[]) => {
                            setValue('client_division_ids', ids, { shouldValidate: true });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {canEditNetwork && (
                    <div className={cn(styles.row, styles.providerOrganizationRow)}>
                      <div className={cn(styles.rowItem, styles.details, styles.big)}>
                        <MUISelectInput
                          name="network"
                          label="Network"
                          options={networkOptions.map((option) => ({
                            id: option.code,
                            value: option.code,
                            label: option.label,
                          }))}
                          wrapperClassName={styles.inputWrapper}
                          errors={errors}
                          value={getValues('network' as any) || ''}
                          onChange={(value: string) => {
                            setValue('network' as any, value || null, { shouldValidate: true });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {false && <SuccessAlert />}
                  <div className={styles.providerActionBtnContainer}>
                    <Button type="submit" className={styles.providerSubmit} disabled={isSavingProfile}>
                      Save changes
                    </Button>
                    <Button type="button" className={styles.providerCancel} onClick={onCancelEditProfile}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  /* Parent AddUserPage already provides Container + #patientDashboardPosition; avoid nested scroll layers. */
  return <>{formContent}</>;
};
export default AddCareAdvisorUserPage;
